import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { OverlayScrollState, OverlayViewState } from "@cuekards/core/ipc";
import type { RecordingStatus } from "@cuekards/core/recording";
import { IDLE_STATUS } from "@cuekards/core/recording";
import { DEFAULT_SCROLL_MODE } from "@cuekards/core/scroll-mode";
import {
  IDLE_VOICE_COMMANDS,
  type VoiceCommandState,
} from "@cuekards/core/voice-commands";
import {
  RecordingBadge,
  StatusBar,
  StopConfirmPrompt,
} from "./prompter-chrome.js";
import {
  anchorLineIndex,
  anchorSweep,
  buildScriptModel,
  clampToLine,
  EMPTY_MODEL,
  lineTokenStates,
  nextContentLine,
  type DisplayLine,
  type ScriptModel,
} from "@cuekards/core/script-model";

/**
 * Where the current line rides before the host has said, with the rest of the
 * script scrolling beneath it (FR-26).
 *
 * A fallback rather than the value, since text-align ticket 01: the anchor is a
 * stored setting now, because where a reader's eyes should land depends on where
 * their webcam is bolted, and it arrives on {@link OverlayViewState}. This is
 * what the first render uses, in the frame before the host has answered — so it
 * is a third, which is what the anchor was for the whole of this product's life
 * and is still what a fresh install stores.
 */
export const ANCHOR_FRACTION = 1 / 3;

/**
 * How long the prompter takes to slide from one anchor to the next.
 *
 * This was `transition: transform 120ms ease-out` in the stylesheet until the
 * pane became a real scroll container (scroll-and-bar ticket 03) and the motion
 * moved to `scrollTop`, which no CSS transition applies to. The number and the
 * easing are unchanged, and so is the reason for them: `MIC_FRAME_MS` is 128, so
 * a slide that outlasts the gap between recognition frames is a prompter still
 * catching up with the last word when the next one arrives.
 */
const SCROLL_EASE_MS = 120;

/** Mirror text's flip (prompter-display ticket 02). Inline rather than a class,
 * because it is a prop's value and not a state of the stylesheet's. Horizontal
 * only: glass reflects left to right, and a rig that also needed the screen
 * upside down would be mounted upside down. */
const MIRRORED: React.CSSProperties = { transform: "scaleX(-1)" };

const DEFAULT_VIEW: OverlayViewState = {
  fontSize: 30,
  anchor: ANCHOR_FRACTION,
  side: 0.5,
  mirror: false,
  lineSpacing: 1,
};
const DEFAULT_SCROLL: OverlayScrollState = {
  mode: DEFAULT_SCROLL_MODE,
  offScript: false,
  wpm: 0,
  speechUnavailable: false,
  playing: false,
};

/**
 * The prompter as a whole surface: a karaoke teleprompter (FR-25/26/29) with its
 * status, its recording badge and its spoken-stop prompt. It builds the same
 * display model the host uses from the loaded script text, and reflects the
 * anchored position the host broadcasts.
 *
 * **The web's root.** The desktop had one of these too until one-window ticket
 * 05, when its prompter became a pane inside a shell that carries the transport
 * and the pane switch — so the same parts are assembled by `PrompterPane` and
 * `AppShell` over there instead. A tab has one surface and needs no shell, which
 * is why this one survives rather than both hosts using the same root.
 *
 * Ticket 08 adds the view state broadcast alongside position, which one-window
 * ticket 03 narrowed to the one thing in it that was never geometry: the live
 * font size. Adjust mode went with the click-through window it made briefly
 * interactive. Text-align ticket 01 put a second such thing beside it — where
 * down the prompter the reading line sits, which is a fraction of the height and
 * so means the same in a tab as in a window.
 */
export function OverlayApp(): JSX.Element {
  const [model, setModel] = useState<ScriptModel>(EMPTY_MODEL);
  const [position, setPosition] = useState(-1);
  const [view, setView] = useState<OverlayViewState>(DEFAULT_VIEW);
  const [scroll, setScroll] = useState<OverlayScrollState>(DEFAULT_SCROLL);
  const [recording, setRecording] = useState<RecordingStatus>(IDLE_STATUS);
  const [voice, setVoice] = useState<VoiceCommandState>(IDLE_VOICE_COMMANDS);

  useEffect(() => {
    let cancelled = false;
    // Pull whatever the host already holds before this view mounted,
    // then follow live updates — so a broadcast that fired before mount is
    // never missed.
    void window.cuekards.getScript().then((s) => {
      if (!cancelled) setModel(s ? buildScriptModel(s.text) : EMPTY_MODEL);
    });
    void window.cuekards.getPosition().then((p) => {
      if (!cancelled) setPosition(p);
    });
    void window.cuekards.getOverlayView().then((v) => {
      if (!cancelled) setView(v);
    });
    void window.cuekards.getScrollState().then((s) => {
      if (!cancelled) setScroll(s);
    });
    void window.cuekards.getRecordingStatus().then((s) => {
      if (!cancelled) setRecording(s);
    });
    void window.cuekards.getVoiceCommandState().then((s) => {
      if (!cancelled) setVoice(s);
    });
    const offScriptLoaded = window.cuekards.onScriptLoaded((p) =>
      setModel(buildScriptModel(p.text)),
    );
    const offPosition = window.cuekards.onPositionChanged((p) => setPosition(p));
    const offView = window.cuekards.onOverlayViewChanged((v) => setView(v));
    const offScroll = window.cuekards.onScrollStateChanged((s) => setScroll(s));
    const offRecording = window.cuekards.onRecordingStatusChanged(setRecording);
    const offVoice = window.cuekards.onVoiceCommandStateChanged(setVoice);
    return () => {
      cancelled = true;
      offScriptLoaded();
      offPosition();
      offView();
      offScroll();
      offRecording();
      offVoice();
    };
  }, []);

  // Font size is applied as a CSS variable so the shortcuts (FR-27) resize the
  // prompt live without re-rendering its structure. Line spacing rides beside
  // it for the same reason (prompter-display ticket 04).
  const style = {
    "--overlay-font-size": `${view.fontSize}px`,
    "--overlay-line-spacing": String(view.lineSpacing),
  } as React.CSSProperties;

  return (
    <div className="overlay" style={style}>
      {model.lines.length === 0 ? (
        <p className="overlay-placeholder">cuekards overlay — no script loaded</p>
      ) : (
        <Prompter
          model={model}
          position={position}
          fontSize={view.fontSize}
          anchor={view.anchor}
          mirror={view.mirror}
          lineSpacing={view.lineSpacing}
        />
      )}
      <StatusBar scroll={scroll} />
      <RecordingBadge status={recording} />
      {voice.awaitingConfirmation && <StopConfirmPrompt />}
    </div>
  );
}

/**
 * The script itself, anchored under the reading line, in a container the reader
 * can sometimes scroll.
 *
 * Exported, unlike the rest of this file's parts, because the browser gate
 * measures it: what that number has to mean is the cost of drawing a script *this*
 * way — a React tree of one span per word, re-rendered every time recognition
 * moves — so it has to be this component and not a stand-in that resembles it.
 *
 * **It owns its own scroll container** (scroll-and-bar ticket 03). The block used
 * to be absolutely positioned inside `.overlay` and moved by `transform`, which
 * left this component reaching out to `parentElement` for the height the anchor is
 * a fraction of — an invariant three comments in this repo had to state and
 * nothing could enforce. A scroll container has to be an element of its own
 * anyway, because the status chip and the corner must *not* scroll with the
 * script, so the reach upwards is gone: what this measures is what it renders.
 */
export function Prompter({
  model,
  position,
  fontSize,
  anchor,
  mirror = false,
  lineSpacing = 1,
  scrollable = false,
}: {
  model: ScriptModel;
  position: number;
  fontSize: number;
  /**
   * How far down the container the reading line rides, as a fraction of its
   * height (text-align ticket 01).
   *
   * Required, like `fontSize` beside it: both hosts hold this on
   * {@link OverlayViewState} and a default here would be a second opinion about
   * where the words go, silently overriding a reader who had moved them.
   */
  anchor: number;
  /**
   * Whether the script is drawn flipped left to right, for a screen read
   * through teleprompter glass (prompter-display ticket 02).
   *
   * The block flips and nothing else does. Not the scroller, which would move
   * the scrollbar gutter to the left, and not `.overlay`, which would flip the
   * status chip and the recording badge with it. A transform is safe to put on
   * the block because everything the anchor is measured from is `offsetTop`,
   * `offsetHeight`, `clientHeight` and `scrollTop`, and a transform changes none
   * of them, so no dependency is added to the layout effect below.
   *
   * Optional where `anchor` is required, and defaulting to off: the browser
   * gate renders this component directly, and what it measures is the cost of
   * drawing a script, which a flip does not change. Both prompters that read
   * {@link OverlayViewState} pass it.
   */
  mirror?: boolean;
  /**
   * How far apart the lines are drawn, as a factor on the stylesheet's own
   * line height (prompter-display ticket 04).
   *
   * Not drawn from here: the spacing is `--overlay-line-spacing` on `.overlay`,
   * beside the font size, and the stylesheet multiplies it in. It is a prop
   * only so that the layout effect below can depend on it, for the font size's
   * reason: it re-flows every line, so the anchor has to be measured again.
   *
   * Optional and 1 by default, for `mirror`'s reason: the browser gate renders
   * this component directly, and draws at the shipped spacing through the
   * custom property without passing this.
   */
  lineSpacing?: number;
  /**
   * Whether the reader may scroll the script by hand right now — `scrollbarLive`
   * in the host, and false everywhere the prompter is the whole surface.
   *
   * It decides two things at once, and they are the same thing seen from either
   * end. While locked, the view is the anchor's: every follower step slides it.
   * While live, the view is the reader's, and this stops writing `scrollTop`
   * except when the anchor is moved *by hand* — a nudge, a re-sync, back to the
   * top — because by the rule that made this true, nothing else can be moving it.
   */
  scrollable?: boolean;
}): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const blockRef = useRef<HTMLDivElement>(null);
  const lineRefs = useRef<(HTMLParagraphElement | null)[]>([]);
  const lastModel = useRef<ScriptModel | null>(null);
  const lastPosition = useRef(position);
  const lastScrollable = useRef(scrollable);
  const lastAnchor = useRef(anchor);
  const lastLineSpacing = useRef(lineSpacing);
  const tween = useRef<number | null>(null);
  const anchorLine = anchorLineIndex(model, position);

  // Scroll the container so the anchored line rides at the anchor, swept by how
  // far through that line the current word is (`anchorSweep`) so every word moves
  // the page a little instead of every line moving it a lot. Measured against the
  // real laid-out DOM, so it holds regardless of how the script wraps at the
  // current width. useLayoutEffect writes it before paint, so there is no flash
  // of the block at its unscrolled origin. `fontSize` is a dependency because
  // changing it re-flows every line height, so the anchor must be recomputed
  // against the new layout; `lineSpacing` is one for the same reason
  // (prompter-display ticket 04); `anchor` is one for the plainer reason that it
  // is the number this whole effect is about.
  useLayoutEffect(() => {
    // Drop refs for lines that no longer exist when a shorter script loads.
    lineRefs.current.length = model.lines.length;

    const scroller = scrollRef.current;
    const block = blockRef.current;
    const line = anchorLine >= 0 ? lineRefs.current[anchorLine] : null;

    // A new script is a re-anchor onto unrelated content, not a follow step: snap
    // to it instantly rather than sliding across the whole document, which would
    // read as the page-turn FR-26 forbids. Within a script, position nudges keep
    // the smooth slide.
    const reanchor = lastModel.current !== model;
    const moved = lastPosition.current !== position;
    const handedOver = lastScrollable.current !== scrollable;
    const realigned = lastAnchor.current !== anchor;
    const respaced = lastLineSpacing.current !== lineSpacing;
    lastModel.current = model;
    lastPosition.current = position;
    lastScrollable.current = scrollable;
    lastAnchor.current = anchor;
    lastLineSpacing.current = lineSpacing;
    if (!scroller || !block) return;

    // The padding is what lets the first and last lines ride the anchor wherever
    // the reader has put it, which is where they rode when the block was
    // absolutely positioned and free to be translated past either edge. Written
    // here rather than in the stylesheet because it is a fraction of the
    // container's height, which CSS cannot express and this effect has already
    // measured — and, since text-align ticket 01, a fraction that moves.
    const height = scroller.clientHeight;
    block.style.paddingTop = `${height * anchor}px`;
    block.style.paddingBottom = `${height * (1 - anchor)}px`;
    if (!line) return;

    // The gap the sweep is a fraction of is centre-to-centre to the next content
    // line, which is what makes the sweep land exactly where the next line's
    // resting place is. The last line of the script has no next one, so it sweeps
    // across its own height instead — the same feel, and the alternative is the
    // final line being the one line that does not move.
    const centre = (el: HTMLElement): number => el.offsetTop + el.offsetHeight / 2;
    const nextIndex = nextContentLine(model, anchorLine);
    const next = nextIndex >= 0 ? lineRefs.current[nextIndex] : null;
    const step = next ? centre(next) - centre(line) : line.offsetHeight;

    const anchorY = height * anchor;
    const lineCentre = centre(line) + anchorSweep(model, position) * step;
    const target = lineCentre - anchorY;

    // A view the reader has scrolled is theirs until they say otherwise. The only
    // thing that can move the anchor while the scrollbar is live is a hand on a
    // key — no follower, no timer, by the rule that made it live — so a position
    // that has not changed means this render was about something else, and the
    // view stays where it was put.
    //
    // A realignment is not in that list on purpose. The padding above has already
    // moved the words, and `target` is the same number either side of an anchor
    // change — the padding is inside `centre(line)` and `anchorY` takes it back
    // out — so writing it here would only yank a reader who had scrolled away
    // back to the anchor for a press that was about something else.
    if (scrollable && !reanchor && !moved && !handedOver) return;

    // Instant for anything that is not a follow step: a new script, the hand-off
    // as the scrollbar locks or unlocks, a realignment, a respacing, and every
    // deliberate move made while the view was the reader's. Sliding for those
    // would be watching the prompter travel somewhere it should simply be — and
    // for a realignment that lands in the same render as a follow step, it would
    // be a glide across a jump the padding has already made underneath it. A
    // respacing is the same: the lines have already moved under the reading line
    // by the time this runs, and easing back would be a visible lurch.
    if (reanchor || handedOver || scrollable || realigned || respaced) {
      cancelTween(tween);
      scroller.scrollTop = target;
      return;
    }
    easeScrollTo(scroller, target, tween);
  }, [model, position, anchorLine, fontSize, lineSpacing, anchor, scrollable]);

  // A slide left running past unmount would write to a detached element every
  // frame until it finished.
  useEffect(() => () => cancelTween(tween), []);

  return (
    <div
      className={`overlay-scroll${scrollable ? " scrollable" : ""}`}
      ref={scrollRef}
    >
      <div
        className="overlay-block"
        ref={blockRef}
        style={mirror ? MIRRORED : undefined}
      >
        {model.lines.map((line, i) => (
          <PrompterLine
            key={i}
            index={i}
            line={line}
            position={clampToLine(line, position)}
            lineRefs={lineRefs}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Slide the scroller to `target` over {@link SCROLL_EASE_MS}.
 *
 * What `transition: transform` used to do for free, and has to be written out now
 * that the motion is `scrollTop` — which no CSS transition applies to, and whose
 * one native alternative (`scroll-behavior: smooth`) picks its own duration.
 *
 * The curve is an ease-out quadratic. CSS `ease-out` is `cubic-bezier(0, 0, .58,
 * 1)`, which this is not exactly; across a 120 ms slide of one line height the two
 * are a few pixels apart for a frame or two in the middle, which is a smaller
 * error than the one already in the thing being animated — the trailing word is
 * provisional (ADR-0020) and can be revised out from under the slide entirely.
 */
function easeScrollTo(
  scroller: HTMLElement,
  target: number,
  tween: React.MutableRefObject<number | null>,
): void {
  cancelTween(tween);
  const from = scroller.scrollTop;
  const distance = target - from;
  // Sub-pixel steps are what a long word costs; animating one is a frame of work
  // for something no eye resolves.
  if (Math.abs(distance) < 1) {
    scroller.scrollTop = target;
    return;
  }
  const started = performance.now();
  const step = (now: number): void => {
    const t = Math.min(1, (now - started) / SCROLL_EASE_MS);
    scroller.scrollTop = from + distance * (1 - (1 - t) * (1 - t));
    tween.current = t < 1 ? requestAnimationFrame(step) : null;
  };
  tween.current = requestAnimationFrame(step);
}

function cancelTween(tween: React.MutableRefObject<number | null>): void {
  if (tween.current !== null) {
    cancelAnimationFrame(tween.current);
    tween.current = null;
  }
}

/**
 * One line of the prompter, memoised.
 *
 * A position arrives about eight times a second, and without this every one of
 * them re-rendered every word of the script — two spans per word once the gaps
 * are counted, so a fourteen-hundred-word doc rebuilt some five thousand elements
 * to move one highlight by one word. That work lands on the renderer's main
 * thread, which is also the thread relaying microphone frames from the audio
 * worklet to the recogniser (see `audio-tap.ts`, which moved the capture *off*
 * this thread for exactly this reason). Reconciliation there is advance latency
 * with extra steps.
 *
 * With the position clamped by `clampToLine` (core, since mobile-app ticket 37,
 * because the phone draws the same way), a normal advance re-renders the line
 * the reader is on and the line they just left, and nothing else.
 */
const PrompterLine = memo(function PrompterLine({
  index,
  line,
  position,
  lineRefs,
}: {
  index: number;
  line: DisplayLine;
  /** Already clamped by {@link clampToLine} — not the raw script position. */
  position: number;
  /**
   * The parent's ref array, handed over whole rather than as a per-line callback.
   * A callback built in the parent's render would be a new function every time
   * and every prop comparison would fail on it, which is the memo not memoising —
   * the array is one stable object, so the comparison is on `index` instead.
   */
  lineRefs: { current: (HTMLParagraphElement | null)[] };
}): JSX.Element {
  return (
    <p
      ref={(el) => {
        lineRefs.current[index] = el;
      }}
      className="overlay-line"
    >
      {renderLine(line, position)}
    </p>
  );
});

/** Render one line's tokens: words coloured by whether they are spoken, current
 * or upcoming; notes dimmed; and the punctuation and spacing between them drawn
 * exactly as the script wrote it. A blank line renders a non-breaking space so it
 * keeps its height as a paragraph gap.
 *
 * Nothing is inserted between tokens any more. This used to join them with a
 * single space, and that is where every comma and full stop in a loaded Google
 * Doc went: a word's source span stops at its last letter, so the punctuation
 * after it belonged to no token and was never drawn. It is a `gap` token now. */
function renderLine(line: DisplayLine, position: number): React.ReactNode {
  if (line.tokens.length === 0) return " ";
  // Which state each token is in — punctuation taking the colour of the word it
  // follows, and the gap after the current word counting as spoken — is core's
  // `lineTokenStates`, shared with the phone (mobile-app ticket 37). What is
  // decided here is only the class names.
  const states = lineTokenStates(line, position);
  return line.tokens.map((token, i) => {
    if (token.kind === "note") {
      return (
        <span className="note" key={i}>
          {token.text}
        </span>
      );
    }
    // A gap is `gap`, deliberately not `word`: `.overlay-line .word` is how the
    // browser gate and the shortcut tests enumerate the script's spoken words,
    // and a comma answering to that selector would quietly corrupt every one of
    // them. The colours are shared in CSS instead.
    return (
      <span className={`${token.kind} ${states[i]!}`} key={i}>
        {token.text}
      </span>
    );
  });
}
